import { css, cx } from "@emotion/css";
import moment from "moment/moment";
import type { FC, MutableRefObject } from "react";
import {
  useContext,
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { useMutation, useQueryClient } from "react-query";

import { Colors, Typography, Box } from "@analyticsMD/quil";

import {
  styles as commonStyles,
  sxStyles as commonSxStyles,
} from "./common-styles";
import EDDSelector from "../edd-selector";
import MedicallyReadySection from "./medically-ready-section";
import SuggestionConfirmation from "../suggestion-confirmation";
import DispositionSelector from "../disposition-selector";
import APIContext from "../../contexts/APIContext";
import { patchEDD, updatePatientDispositions } from "../../api/endpoints";
import IntelligenceTextDisplay from "../intelligence-text";
import { convertDateToUTC } from "../../utils/utils";
import { TypeChoices } from "../../types/TypeChoices";
import { ActionTrack } from "../../types/Analytics";
import type Disposition from "../../types/Disposition";
import EDD from "../../types/EDD";
import UpdateHistory from "../update-history";
import Configuration from "../../types/Configuration";
import InfoIcon from "@mui/icons-material/Info";

const styles = {
  ...commonStyles,
  dischargePlanSection: css`
    background-color: ${Colors.white};
    padding-top: 0.5rem;
    padding-bottom: 0;
  `,
  selector: (useWideLayout) => css`
    display: flex;
    flex-direction: row;
    align-items: center;
    margin-bottom: 0.625rem;
    align-items: flex-end;
    margin-top: -0.75rem;
    justify-content: flex-start;
    padding-left: ${useWideLayout ? "0" : "2.6rem"};
    margin-right: 1rem !important;
  `,
  suggestion: css`
    padding-left: 3rem;
  `,
  headerIcon: css`
    margin-right: 0.5rem;
    font-size: 1rem;
  `,
  expandCollapseIcon: css`
    color: ${Colors.grey_40};
  `,
  medicallyReadyTimestamp: (useWideLayout, isLgBreakpoint) => css`
    align-self: center;
    padding-left: ${useWideLayout && !isLgBreakpoint ? "3rem" : "0.85rem"};
  `,
  dischargeSection: css`
    padding-top: 0.5rem;
  `,
};

const sxStyles = {
  ...commonSxStyles,
  headingWithIcon: {
    svg: {
      color: Colors.qlavender_30,
    },
  },
  headingWithHistory: {
    display: "flex",
  },
  headingWrapper: {
    display: "flex",
    justifyContent: "space-between",
  },
  timestamp: {
    fontSize: "0.8rem",
    marginLeft: "1.5rem",
  },
};

interface DischargePlanSectionProps {
  useSidebarView?: boolean;
  updateRef?: MutableRefObject<Record<string, any>>; // ref to hold optimistic updates for patient list
}

const DischargePlanSection: FC<DischargePlanSectionProps> = ({
  useSidebarView,
  updateRef,
}) => {
  const eddRowAnchorRef = useRef<HTMLDivElement>(null);
  const dispositionRowAnchorRef = useRef<HTMLDivElement>(null);

  const {
    configurationQuery,
    patientID,
    dispositionsQuery,
    patientDispositionsQuery,
    eddQuery,
    intelligenceTextQuery,
    analytics,
    setShowErrorToast,
    isViewOnly,
    patientDataReadOnly,
  } = useContext(APIContext);

  const [currentDispositions, setCurrentDispositions] = useState<Disposition[]>(
    [],
  );
  const [isDischargeAM, setIsDischargeAM] = useState(false);
  const [eddSuggestionActionTaken, setEddSuggestionActionTaken] =
    useState(false);
  const [dispoSuggestionActionTaken, setDispoSuggestionActionTaken] =
    useState(false);

  const configuration: Configuration = configurationQuery?.data
    ? configurationQuery?.data?.[0]
    : {};

  const currentUserEDD =
    eddQuery.isSuccess &&
    eddQuery.data?.find((edd) => edd.edd_type === TypeChoices.USER_ENTERED);

  const [currentEDD, setCurrentEDD] = useState(
    currentUserEDD ? currentUserEDD.edd_value : null,
  );

  const selectableDispositions = dispositionsQuery.data as Disposition[];

  const currentUserEnteredDispositions = useMemo(() => {
    return (
      patientDispositionsQuery?.data.filter(
        (disposition) =>
          disposition.disposition_type === TypeChoices.USER_ENTERED,
      ) || []
    );
  }, [patientDispositionsQuery.data]);

  // Only sync on initial load, not on every refetch
  const hasInitializedRef = useRef(false);
  useEffect(() => {
    if (
      !hasInitializedRef.current &&
      currentUserEnteredDispositions.length >= 0
    ) {
      const dispos = currentUserEnteredDispositions.map((pd) => pd.disposition);
      setCurrentDispositions(dispos);
      hasInitializedRef.current = true;
    }
  }, [currentUserEnteredDispositions]);

  const suggestedPatientDisposition =
    patientDispositionsQuery.isSuccess &&
    patientDispositionsQuery.data?.filter(
      (disType) =>
        disType.disposition_type === TypeChoices.SMART_SUGGESTION &&
        disType.status === null,
    );

  const suggestedEdd =
    eddQuery.isSuccess &&
    eddQuery.data?.filter(
      (eddType) =>
        eddType.edd_type === TypeChoices.SMART_SUGGESTION &&
        eddType.status === null,
    );

  // get the most recent suggestion (actioned or not)
  const latestSuggestedEdd =
    eddQuery.isSuccess &&
    eddQuery.data?.find((e) => e.edd_type === TypeChoices.SMART_SUGGESTION);

  const latestSuggestedDisposition =
    patientDispositionsQuery.isSuccess &&
    patientDispositionsQuery.data?.find(
      (d) => d.disposition_type === TypeChoices.SMART_SUGGESTION,
    );

  // Ensure suggestedPatientDisposition is always an array for safe length access
  const suggestedPatientDispositionArray = Array.isArray(
    suggestedPatientDisposition,
  )
    ? suggestedPatientDisposition
    : [];
  const suggestedEddArray = Array.isArray(suggestedEdd) ? suggestedEdd : [];

  let EDDDisplayFirst = suggestedEddArray.length > 0;
  let PatientDispoDisplayFirst = suggestedPatientDispositionArray.length > 0;

  let eddIntelText = null;
  let dispoIntelText = null;

  // get the latest intelligence text, if any
  if (intelligenceTextQuery.isSuccess && intelligenceTextQuery.data) {
    eddIntelText = intelligenceTextQuery.data.find(
      (it) => it.intelligence_category === "EDD",
    );
    dispoIntelText = intelligenceTextQuery.data.find(
      (it) => it.intelligence_category === "DISPOSITION",
    );
  }

  let hideEddIntelText = false;
  if (suggestedEddArray.length > 0 && eddIntelText) {
    EDDDisplayFirst =
      new Date(suggestedEddArray[0].qv_created_ts) >
      new Date(eddIntelText.qv_created_ts);

    // if the most recent suggestion has been accepted/rejected, hide text
    hideEddIntelText =
      latestSuggestedEdd &&
      new Date(latestSuggestedEdd.qv_created_ts) >
        new Date(eddIntelText.qv_created_ts) &&
      latestSuggestedEdd.status;
  }

  let hideDispoIntelText = false;
  if (suggestedPatientDispositionArray.length > 0 && dispoIntelText) {
    PatientDispoDisplayFirst =
      new Date(suggestedPatientDispositionArray[0].qv_created_ts) >
      new Date(dispoIntelText.qv_created_ts);

    // if the most recent suggestion has been accepted/rejected, hide text
    hideDispoIntelText =
      latestSuggestedDisposition &&
      new Date(latestSuggestedDisposition.qv_created_ts) >
        new Date(dispoIntelText.qv_created_ts) &&
      latestSuggestedDisposition.status;
  }

  const queryClient = useQueryClient();

  const updatePatientDispositionMutation = useMutation(
    updatePatientDispositions,
    {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["patientdispositions", patientID],
        });
      },
      onError: () => {
        // revert the change if the mutation fails
        const currentPatientDispos =
          (patientDispositionsQuery.isSuccess &&
            patientDispositionsQuery?.data
              .filter(
                (disposition) =>
                  disposition.disposition_type === TypeChoices.USER_ENTERED,
              )
              .map((pd) => pd.disposition)) ||
          [];
        setCurrentDispositions(currentPatientDispos);
        setDispoSuggestionActionTaken(false);
        setShowErrorToast(true);
      },
    },
  );

  const setDispositionUpdateRef = useCallback(
    (dispositions) => {
      const sortedDispositions = dispositions.sort((a, b) =>
        a.display_name.localeCompare(b.display_name),
      );

      // Only update ref when NOT in sidebar view
      if (updateRef && !useSidebarView) {
        updateRef.current = {
          ...updateRef.current,
          dispositions: sortedDispositions,
          patient: patientID,
        };
      }
    },
    [updateRef, useSidebarView, patientID],
  );

  const patientDispositionClick = (status: string, suggestion: any) => {
    if (status === "ACCEPTED") {
      setCurrentDispositions([suggestion.disposition]);
    }
    setDispoSuggestionActionTaken(true);

    updatePatientDispositionMutation.mutate({
      disposition: suggestion.disposition.id,
      status: status,
      id: suggestion.id,
    });

    setDispositionUpdateRef([suggestion.disposition]);

    analytics?.track(ActionTrack.SUGGESTION_ACTION, {
      type: "Disposition",
      suggestion_id: suggestion.id,
      suggestion_value: suggestion.disposition.display_name,
      status,
    });
  };

  const updatePatientEDDMutation = useMutation(patchEDD, {
    onSuccess: (data) => {
      // Update the edd query when this mutation succeeds
      queryClient.invalidateQueries({ queryKey: ["edd", patientID] });
    },
    onError: () => {
      // revert the change if the mutation fails
      const currentUserEDD =
        eddQuery.isSuccess &&
        eddQuery.data?.find((edd) => edd.edd_type === TypeChoices.USER_ENTERED);
      if (currentUserEDD) {
        const currentUserEDDFormatted = moment(currentUserEDD.edd_value).format(
          "MM/DD/YYYY",
        );
        setCurrentEDD(currentUserEDDFormatted);
        setEddSuggestionActionTaken(false);
      }
      setShowErrorToast(true);
    },
  });

  const setEDDUpdateRef = (edd) => {
    const eddValue = convertDateToUTC(new Date(edd));
    const formattedValue = moment(eddValue).format("MM/DD/YYYY");

    // update the ref, if needed
    if (updateRef && !useSidebarView) {
      updateRef.current = {
        ...updateRef.current,
        edd: formattedValue,
        patient: patientID,
      };
    }
  };

  const patientEddClick = (status: string, suggestion: EDD) => {
    const edd = moment(suggestion.edd_value).format("YYYY-MM-DD");

    if (status === "ACCEPTED") {
      const e = convertDateToUTC(new Date(edd));
      setCurrentEDD(e);
      setIsDischargeAM(suggestion.is_am_discharge);
    }

    setEddSuggestionActionTaken(true);

    updatePatientEDDMutation.mutate({
      id: suggestion.id,
      edd_value: edd,
      status: status,
    });

    setEDDUpdateRef(edd);

    analytics?.track(ActionTrack.SUGGESTION_ACTION, {
      type: "Edd",
      suggestion_id: suggestion.id,
      suggestion_value: edd,
      status,
    });
  };

  const handleSetCurrentEDD = (edd_value) => {
    const edd = moment(edd_value).format("YYYY-MM-DD");
    const e = convertDateToUTC(new Date(edd));
    setCurrentEDD(e);
    setEddSuggestionActionTaken(true);
    setEDDUpdateRef(edd);
  };

  const handleSetCurrentDispositions = useCallback(
    (dispositions, isUserAction = true) => {
      setCurrentDispositions(dispositions);
      // Only set the flag and update ref if this is a user action, not when syncing from API
      if (isUserAction) {
        setDispoSuggestionActionTaken(true);
        setDispositionUpdateRef(dispositions);
      }
    },
    [setDispositionUpdateRef],
  );

  // if we receive new data we should be able to show it.
  // even after action is taken
  useEffect(() => {
    setEddSuggestionActionTaken(false);
    setDispoSuggestionActionTaken(false);
  }, [
    eddIntelText,
    dispoIntelText,
    suggestedEddArray.length,
    suggestedPatientDispositionArray.length,
  ]);

  return (
    <Box className={cx(styles.roundedSection, styles.dischargePlanSection)}>
      <Box sx={sxStyles.headingWrapper}>
        <Typography
          sx={{ ...sxStyles.sectionHeading, ...sxStyles.headingWithIcon }}
        >
          <InfoIcon className={styles.headerIcon} />
          <span>Early Discharge Plan</span>
        </Typography>
        <Box sx={sxStyles.headingWithHistory}>
          {configuration.update_history_enabled && <UpdateHistory />}
        </Box>
      </Box>
      <Box>
        <Box>
          <Box className={styles.dischargeSection}>
            <EDDSelector
              currentEDD={currentEDD}
              setCurrentEDD={handleSetCurrentEDD}
              currentUserEDD={currentUserEDD}
              patientID={patientID}
              isDischargeAM={isDischargeAM}
              setIsDischargeAM={setIsDischargeAM}
              intelText={eddIntelText}
              enableDischargeTimeOfDay={
                configuration.discharge_time_of_day_enabled
              }
              analytics={analytics}
              setShowErrorToast={setShowErrorToast}
              disabled={patientDataReadOnly}
              hasSuggestion={
                !isViewOnly &&
                eddQuery.isSuccess &&
                suggestedEdd.length > 0 &&
                !eddSuggestionActionTaken &&
                EDDDisplayFirst
              }
            />
          </Box>
          <Box ref={eddRowAnchorRef} sx={{ width: "100%", minWidth: 0 }}>
            {!isViewOnly &&
            eddQuery.isSuccess &&
            suggestedEdd.length > 0 &&
            !eddSuggestionActionTaken &&
            EDDDisplayFirst ? (
              <SuggestionConfirmation
                suggestion={suggestedEdd[0]}
                suggestionType={"edd"}
                updateFn={patientEddClick}
                disabled={patientDataReadOnly}
                explainabilityAnchorRef={eddRowAnchorRef}
              />
            ) : !isViewOnly &&
              eddIntelText &&
              !EDDDisplayFirst &&
              !eddSuggestionActionTaken &&
              !hideEddIntelText ? (
              <IntelligenceTextDisplay
                intelligenceText={eddIntelText}
                popoverAnchorRef={eddRowAnchorRef}
              />
            ) : (
              <></>
            )}
          </Box>
        </Box>
        <Box>
          <Box className={styles.dischargeSection}>
            <DispositionSelector
              currentDispositions={currentDispositions}
              setCurrentDispositions={handleSetCurrentDispositions}
              selectableDispositions={selectableDispositions}
              currentUserEnteredDispositions={currentUserEnteredDispositions}
              patientID={patientID}
              isMultiDispoEnabled={configuration.multiple_dispositions_enabled}
              analytics={analytics}
              setShowErrorToast={setShowErrorToast}
              disabled={patientDataReadOnly}
              hasSuggestion={
                !isViewOnly &&
                patientDispositionsQuery.isSuccess &&
                suggestedPatientDispositionArray.length > 0 &&
                !dispoSuggestionActionTaken &&
                PatientDispoDisplayFirst
              }
              useSidebarView={useSidebarView}
            />
          </Box>
          <Box ref={dispositionRowAnchorRef} sx={{ width: "100%", minWidth: 0 }}>
            {!isViewOnly &&
            patientDispositionsQuery.isSuccess &&
            suggestedPatientDispositionArray.length > 0 &&
            !dispoSuggestionActionTaken &&
            PatientDispoDisplayFirst ? (
              <SuggestionConfirmation
                suggestion={suggestedPatientDispositionArray[0]}
                suggestionType={"disposition"}
                updateFn={patientDispositionClick}
                disabled={patientDataReadOnly}
                explainabilityAnchorRef={dispositionRowAnchorRef}
              />
            ) : !isViewOnly &&
              dispoIntelText &&
              !PatientDispoDisplayFirst &&
              !dispoSuggestionActionTaken &&
              !hideDispoIntelText ? (
              <IntelligenceTextDisplay
                intelligenceText={dispoIntelText}
                popoverAnchorRef={dispositionRowAnchorRef}
              />
            ) : (
              <></>
            )}
          </Box>
        </Box>
        <Box className={styles.dischargeSection}>
          {configuration.medically_ready_section_enabled && (
            <Box>
              <MedicallyReadySection
                updateRef={updateRef}
                useSidebarView={useSidebarView}
              />
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default DischargePlanSection;
